import React, { useEffect, useState } from "react";
import { User } from "./types";
import { supabase } from "./services/supabaseClient";

import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { Dashboard } from "./pages/Dashboard";
import { Questions } from "./pages/Questions";
import { Admin } from "./pages/Admin";
import { Sidebar } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { FlashcardsPage } from "./pages/FlashcardsPage";
import { SimuladosPage } from "./pages/SimuladosPage";
import { ProgressoPage } from "./pages/ProgressoPage";
import { PremiumCheckout } from "./pages/PremiumCheckout";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { ContactPage } from "./pages/ContactPage";

type Page =
  | "landing"
  | "login"
  | "signup"
  | "terms"
  | "privacy"
  | "contact"
  | "dashboard"
  | "questions"
  | "flashcards"
  | "simulados"
  | "progresso"
  | "admin"
  | "premium-checkout";

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<Page>("landing");
  const [pageParams, setPageParams] = useState<any>(null);
  const [userProgress, setUserProgress] = useState<any[]>([]);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const isPublicPage = (p: Page) =>
    ["landing", "login", "signup", "terms", "privacy", "contact"].includes(p);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase.from("users").select("*").eq("id", userId).single();
    if (error) throw error;
    return data as User;
  };

  const fetchUserProgress = async (userId: string) => {
    const { data, error } = await supabase.from("user_progress").select("*").eq("user_id", userId);
    if (error) throw error;
    return data ?? [];
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data.session;

        if (session?.user?.id) {
          const profile = await fetchProfile(session.user.id);
          const progress = await fetchUserProgress(session.user.id);

          if (!mounted) return;
          setUser(profile);
          setUserProgress(progress);
          setPage(profile.role === "admin" ? "admin" : "dashboard");
        }
      } catch (e) {
        if (!mounted) return;
        setUser(null);
        setUserProgress([]);
        setPage("landing");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        if (event === "SIGNED_OUT" || !session?.user?.id) {
          setUser(null);
          setUserProgress([]);
          setPage("landing");
          setPageParams(null);
          setMobileSidebarOpen(false);
          return;
        }

        const profile = await fetchProfile(session.user.id);
        const progress = await fetchUserProgress(session.user.id);

        setUser(profile);
        setUserProgress(progress);
        setPage(profile.role === "admin" ? "admin" : "dashboard");
        setMobileSidebarOpen(false);
      } catch {
        setUser(null);
        setUserProgress([]);
        setPage("landing");
        setMobileSidebarOpen(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleNavigate = (newPage: Page, params: any = null) => {
    setPage(newPage);
    setPageParams(params);
    setMobileSidebarOpen(false);
    window.scrollTo(0, 0);
  };

  const handleLogin = async (loggedUser: User) => {
    setUser(loggedUser);
    setPage(loggedUser.role === "admin" ? "admin" : "dashboard");
    setMobileSidebarOpen(false);

    try {
      const progress = await fetchUserProgress(loggedUser.id);
      setUserProgress(progress);
    } catch {
      setUserProgress([]);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setUserProgress([]);
    setPage("landing");
    setPageParams(null);
    setMobileSidebarOpen(false);
  };

  const handleXPUpdate = async (amount: number, themeId?: number, activityTitle?: string) => {
    if (!user) return;

    const { data: updatedUser, error: userErr } = await supabase
      .from("users")
      .update({ xp: (user.xp ?? 0) + amount })
      .eq("id", user.id)
      .select("*")
      .single();

    if (userErr) return;

    setUser(updatedUser as User);

    if (themeId) {
      await supabase.from("user_progress").upsert(
        {
          user_id: user.id,
          theme_id: themeId,
          xp_earned: amount,
          questions_completed: 0,
        },
        { onConflict: "user_id,theme_id" }
      );
    }

    await supabase.from("activities").insert({
      user_id: user.id,
      type: "xp",
      title: activityTitle ?? "XP",
      xp_gain: amount,
    });

    try {
      const progress = await fetchUserProgress(user.id);
      setUserProgress(progress);
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="font-black uppercase tracking-widest text-xs">Conectando ao Academy...</p>
      </div>
    );
  }

  const renderPage = () => {
    if (!isPublicPage(page) && !user) {
      return <Login onLogin={handleLogin} onNavigate={handleNavigate} />;
    }

    switch (page) {
      case "landing":
        return <Landing onNavigate={handleNavigate} />;
      case "login":
        return <Login onLogin={handleLogin} onNavigate={handleNavigate} />;
      case "signup":
        return <Signup onLogin={handleLogin} onNavigate={handleNavigate} />;
      case "terms":
        return <TermsPage onNavigate={handleNavigate} />;
      case "privacy":
        return <PrivacyPage onNavigate={handleNavigate} />;
      case "contact":
        return <ContactPage onNavigate={handleNavigate} />;
      case "dashboard":
        return user ? <Dashboard user={user} onNavigate={handleNavigate} /> : null;
      case "questions":
        return user ? (
          <Questions
            user={user}
            themeId={pageParams?.themeId}
            onNavigate={handleNavigate}
            onXPUpdate={handleXPUpdate}
          />
        ) : null;
      case "flashcards":
        return user ? (
          <FlashcardsPage user={user} onNavigate={handleNavigate} onXPUpdate={handleXPUpdate} />
        ) : null;
      case "simulados":
        return user ? (
          <SimuladosPage user={user} onNavigate={handleNavigate} onXPUpdate={handleXPUpdate} />
        ) : null;
      case "progresso":
        return user ? <ProgressoPage user={user} onNavigate={handleNavigate} /> : null;
      case "admin":
        return user ? <Admin user={user} onNavigate={handleNavigate} /> : null;
      case "premium-checkout":
        return user ? (
          <PremiumCheckout user={user} onSuccess={handleLogin} onCancel={() => setPage("dashboard")} />
        ) : null;
      default:
        return <Landing onNavigate={handleNavigate} />;
    }
  };

  const showSidebar =
    !!user &&
    !["landing", "login", "signup", "premium-checkout", "admin", "terms", "privacy", "contact"].includes(
      page
    );

  const showTopBar =
    !!user && !["landing", "login", "signup", "premium-checkout", "terms", "privacy", "contact"].includes(page);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* MOBILE TOPBAR (apenas quando a sidebar existe) */}
      {showSidebar && user && (
        <div className="md:hidden sticky top-0 z-30 bg-white border-b">
          <div className="h-14 px-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className="px-3 py-2 rounded-xl border border-slate-200 text-slate-800 font-black text-sm"
            >
              Menu
            </button>

            <div className="text-slate-800 font-black text-sm">UroInsights</div>

            <button
              type="button"
              onClick={handleLogout}
              className="px-3 py-2 rounded-xl border border-slate-200 text-slate-800 font-black text-sm"
            >
              Sair
            </button>
          </div>
        </div>
      )}

      <div className="min-h-screen md:flex">
        {/* SIDEBAR DESKTOP */}
        {showSidebar && user && (
          <aside className="hidden md:block md:shrink-0">
            <Sidebar
              activePage={page}
              onNavigate={handleNavigate}
              userProgress={userProgress}
              activeParams={pageParams}
            />
          </aside>
        )}

        {/* CONTEÚDO */}
        <main className="flex-1 flex flex-col min-w-0">
          {showTopBar && user && <TopBar user={user} onLogout={handleLogout} />}
          <div className="flex-1 overflow-x-hidden">{renderPage()}</div>
        </main>
      </div>

      {/* DRAWER MOBILE */}
      {showSidebar && user && mobileSidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* overlay */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileSidebarOpen(false)}
          />
          {/* drawer */}
          <div className="absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl">
            <div className="h-14 px-3 flex items-center justify-between border-b">
              <div className="font-black text-slate-800 text-sm">Menu</div>
              <button
                type="button"
                onClick={() => setMobileSidebarOpen(false)}
                className="px-3 py-2 rounded-xl border border-slate-200 text-slate-800 font-black text-sm"
              >
                Fechar
              </button>
            </div>

            <div className="h-[calc(100%-56px)] overflow-y-auto">
              <Sidebar
                activePage={page}
                onNavigate={handleNavigate}
                userProgress={userProgress}
                activeParams={pageParams}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
